/**
 * Offline Indicator Component
 * Shows network status and pending sync items
 */

import React, { useEffect, useState } from 'react';
import { Wifi, WifiOff, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import useOfflineStore from '../store/offlineStore';
import toast from 'react-hot-toast';
import { formatDateTime } from '../utils/dateFormat';

const OfflineIndicator = () => {
  const { 
    isOnline, 
    pendingOrders, 
    isSyncing, 
    syncError,
    lastSyncTime,
    syncPendingOrders,
    setOnlineStatus
  } = useOfflineStore();
  
  const [showDetails, setShowDetails] = useState(false);

  // Check online status on mount and periodically
  useEffect(() => {
    const checkConnection = () => {
      setOnlineStatus(navigator.onLine);
    };
    
    // Check every 30 seconds
    const interval = setInterval(checkConnection, 30000);
    
    return () => clearInterval(interval);
  }, [setOnlineStatus]);

  // Show toast when going offline/online
  useEffect(() => {
    if (!isOnline) {
      toast.error('You are offline. Orders will be saved locally.', {
        duration: 5000,
        icon: '📴'
      });
    }
  }, [isOnline]);

  const handleSync = async () => {
    if (isSyncing) return;
    
    const result = await syncPendingOrders();
    
    if (result.success && result.synced > 0) {
      toast.success(`Synced ${result.synced} orders successfully!`, {
        icon: '✅'
      });
    } else if (result.failed > 0) {
      toast.error(`Failed to sync ${result.failed} orders. Will retry.`, {
        icon: '⚠️'
      });
    }
  };

  const pendingCount = pendingOrders.length;

  // Don't show anything if online and no pending items
  if (isOnline && pendingCount === 0 && !isSyncing) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Main indicator button */}
      <div 
        onClick={() => setShowDetails(!showDetails)}
        className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-lg cursor-pointer transition-all ${
          isOnline 
            ? pendingCount > 0 
              ? 'bg-yellow-500 text-white' 
              : 'bg-green-500 text-white'
            : 'bg-red-500 text-white animate-pulse'
        }`}
      >
        {isOnline ? <Wifi size={18} /> : <WifiOff size={18} />}
        <span className="text-sm font-medium">
          {isOnline ? (
            pendingCount > 0 ? `${pendingCount} pending` : 'Online'
          ) : (
            'Offline Mode'
          )}
        </span>
        {isSyncing && <RefreshCw size={16} className="animate-spin" />}
      </div>

      {/* Details panel */}
      {showDetails && (
        <div className="absolute bottom-12 right-0 w-72 bg-white rounded-lg shadow-xl border p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-gray-800">Network Status</h3>
            <button 
              onClick={() => setShowDetails(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          {/* Status */}
          <div className={`flex items-center gap-2 p-2 rounded-lg mb-3 ${
            isOnline ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}>
            {isOnline ? (
              <>
                <CheckCircle size={18} />
                <span>Connected to server</span>
              </>
            ) : (
              <>
                <AlertCircle size={18} />
                <span>No internet connection</span>
              </>
            )}
          </div>

          {/* Pending orders */}
          {pendingCount > 0 && (
            <div className="mb-3">
              <p className="text-sm text-gray-600 mb-2">
                <strong>{pendingCount}</strong> order(s) waiting to sync
              </p>
              <button
                onClick={handleSync}
                disabled={!isOnline || isSyncing}
                className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
                  isOnline && !isSyncing
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isSyncing ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <RefreshCw size={16} />
                    Sync Now
                  </>
                )}
              </button>
            </div>
          )}

          {/* Sync error */}
          {syncError && (
            <div className="text-xs text-red-600 bg-red-50 p-2 rounded mb-2">
              {syncError}
            </div>
          )}

          {/* Last sync time */}
          {lastSyncTime && (
            <p className="text-xs text-gray-400">
              Last sync: {formatDateTime(lastSyncTime)}
            </p>
          )}

          {/* Offline info */}
          {!isOnline && (
            <div className="mt-3 text-xs text-gray-500 bg-gray-50 p-2 rounded">
              <p className="font-medium mb-1">Offline Mode Active</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Orders are saved locally</li>
                <li>Will auto-sync when back online</li>
                <li>Menu uses cached data</li>
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OfflineIndicator;
